import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { adminApi } from "@/lib/admin";

const BLANK = {
  engine: "backup_ai_engine",
  label: "Backup AI Engine",
  model: "",
  endpoint: "https://openrouter.ai/api/v1",
  priority: 1,
  enabled: true,
  supports_vision: false,
  max_tokens: 4096,
  reasoning_effort: "low",
  api_keys_text: "",
};

const MAIN_ENGINE_IDS = new Set(["avian_deepseek", "deepseek", "nscale_qwen", "gemini", "openai", "sarvam", "claude"]);

export default function RuntimeEngineSetupSection({ onSaved }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const sorted = useMemo(
    () => [...items]
      .filter((item) => !MAIN_ENGINE_IDS.has(item.engine))
      .sort((a, b) => a.priority - b.priority),
    [items]
  );

  const load = () => adminApi.runtimeEngines().then((r) => setItems(r.items || [])).catch((e) => setMsg(e.message));
  useEffect(() => { load(); }, []);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const edit = (item) => setForm({ ...BLANK, ...item, api_keys_text: "" });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg("");
    try {
      const payload = { ...form, priority: Number(form.priority), max_tokens: Number(form.max_tokens) };
      if (!payload.api_keys_text.trim()) delete payload.api_keys_text;
      await adminApi.saveRuntimeEngine(payload);
      setMsg("Saved to Backup AI Engines. Main ladder and evals are unchanged.");
      setForm(BLANK);
      await load();
      onSaved?.();
    } catch (err) {
      setMsg(err.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (engine) => {
    if (!window.confirm(`Delete runtime engine ${engine}?`)) return;
    setBusy(true); setMsg("");
    try { await adminApi.deleteRuntimeEngine(engine); await load(); onSaved?.(); }
    catch (err) { setMsg(err.message || "Delete failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="runtime-engine-setup">
      <form onSubmit={save} className="grid lg:grid-cols-2 gap-4 mb-5">
        <Input id="engine" label="Engine id" value={form.engine} onChange={(v) => update("engine", v)} />
        <Input id="label" label="Admin label" value={form.label} onChange={(v) => update("label", v)} />
        <Input id="model" label="Model slug" value={form.model} onChange={(v) => update("model", v)} wide />
        <Input id="endpoint" label="Endpoint" value={form.endpoint} onChange={(v) => update("endpoint", v)} wide />
        <Input id="priority" label="Backup priority — stored for reference only" type="number" value={form.priority} onChange={(v) => update("priority", v)} />
        <Input id="max-tokens" label="Max tokens" type="number" value={form.max_tokens} onChange={(v) => update("max_tokens", v)} />
        <Input id="reasoning-effort" label="Reasoning effort" value={form.reasoning_effort} onChange={(v) => update("reasoning_effort", v)} />
        <div className="flex items-center gap-5 pt-5">
          <label className="font-mono-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-2">
            <input data-testid="runtime-engine-enabled" type="checkbox" checked={form.enabled} onChange={(e) => update("enabled", e.target.checked)} /> Enabled
          </label>
          <label className="font-mono-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-2">
            <input data-testid="runtime-engine-supports-vision" type="checkbox" checked={form.supports_vision} onChange={(e) => update("supports_vision", e.target.checked)} /> Vision
          </label>
        </div>
        <label className="lg:col-span-2 block">
          <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">API keys — one per line; leave blank to keep existing keys</span>
          <textarea data-testid="runtime-engine-api-keys" rows={4} value={form.api_keys_text} onChange={(e) => update("api_keys_text", e.target.value)} className="mt-2 w-full bg-transparent border border-[var(--border)] p-3 font-mono-ui text-xs outline-none focus:border-[var(--accent)]" />
        </label>
        <button data-testid="runtime-engine-save" disabled={busy} className="lg:col-span-2 py-3 border border-[var(--accent)] text-[var(--accent)] font-mono-ui text-[10px] tracking-[0.28em] uppercase hover:bg-[var(--accent)] hover:text-[var(--bg)] transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="inline h-4 w-4 animate-spin" /> : "Save runtime engine"}
        </button>
      </form>
      {msg && <div data-testid="runtime-engine-message" className="text-sm text-[var(--text-muted)] mb-4">{msg}</div>}
      <div className="grid md:grid-cols-2 gap-3">
        {sorted.length === 0 && (
          <div data-testid="runtime-engine-empty" className="border border-[var(--border)] p-4 font-ui text-sm text-[var(--text-muted)]">
            No backup AI engines saved.
          </div>
        )}
        {sorted.map((item) => (
          <div key={item.engine} data-testid={`runtime-engine-row-${item.engine}`} className="border border-[var(--border)] p-4">
            <div className="flex justify-between gap-3"><b>{item.label}</b><span className="font-mono-ui text-xs">#{item.priority}</span></div>
            <div className="font-mono-ui text-xs text-[var(--text-muted)] break-all mt-1">{item.engine} · {item.model}</div>
            <div className="font-mono-ui text-xs text-[var(--text-dim)] mt-1">{item.key_count} key(s): {(item.key_masks || []).join(", ") || "—"}</div>
            <div className="flex gap-2 mt-3">
              <button data-testid={`runtime-engine-edit-${item.engine}`} onClick={() => edit(item)} className="px-3 py-2 border border-[var(--border)] text-xs uppercase tracking-[0.2em]">Edit</button>
              <button data-testid={`runtime-engine-delete-${item.engine}`} onClick={() => remove(item.engine)} className="px-3 py-2 border border-red-500/60 text-red-500 text-xs uppercase tracking-[0.2em]">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Input({ id, label, value, onChange, type = "text", wide = false }) {
  return (
    <label className={wide ? "lg:col-span-2 block" : "block"}>
      <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">{label}</span>
      <input data-testid={`runtime-engine-${id}`} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full bg-transparent border border-[var(--border)] p-3 font-mono-ui text-xs outline-none focus:border-[var(--accent)]" />
    </label>
  );
}